import { KEYWORD_MATCH_TYPE_OPTIONS, OPERATOR_OPTIONS, TARGET_MATCH_TYPE_OPTIONS } from "./constants";

const makeConditionOptions = (engine = "pricing", entityType = "advertisement", { enhancedTagOptions = [] } = {}) => {
  const makeTargetConditionOption = (accessor, label, format = "multi-select") => {
    let matchTypeOptions = [];

    if (accessor === "keyword_match_types") {
      matchTypeOptions = KEYWORD_MATCH_TYPE_OPTIONS;
    }

    if (accessor === "target_match_types") {
      matchTypeOptions = TARGET_MATCH_TYPE_OPTIONS;
    }

    if (accessor === "tags_enhanced") {
      matchTypeOptions = enhancedTagOptions;
    }

    return {
      group: accessor,
      value: `\${${accessor}}`,
      label,
      activeLabel: label,
      format: "multi-select",
      selectOptions: matchTypeOptions,
      supportedOperators: OPERATOR_OPTIONS[format],
    };
  };

  const makeSearchQueryConditionOption = (accessor, label) => {
    return {
      group: accessor,
      supportedOperators: OPERATOR_OPTIONS["text"],
      value: `\${${accessor}}`,
      label,
      showScope: false,
    };
  };

  const makeBiddingConditionOption = (group, accessor, label, format = "number", showScope = true, symbol = null) => {
    return {
      group: group,
      supportedOperators: OPERATOR_OPTIONS[format],
      value: `\${${accessor}}`,
      label,
      symbol,
      showScope,
    };
  };

  const makeTaggingConditionOptions = (type) => {
    const metric = {
      acos: makeBiddingConditionOption(
        "acos",
        "acos",
        ($) => $.programs.metric_option["ACOS"],
        "number",
        true,
        "percent",
      ),
      impressions: makeBiddingConditionOption(
        "impressions",
        "impressions",
        ($) => $.programs.metric_option["Impressions"],
        "number",
        true,
      ),
      clicks: makeBiddingConditionOption("clicks", "clicks", ($) => $.programs.metric_option["Clicks"], "number", true),
      cost: makeBiddingConditionOption(
        "cost",
        "cost",
        ($) => $.programs.metric_option["Cost"],
        "number",
        true,
        "currency",
      ),
      conversions: makeBiddingConditionOption(
        "conversions",
        "conversions",
        ($) => $.programs.metric_option["Conversions"],
        "number",
        true,
      ),
      sales: makeBiddingConditionOption(
        "sales",
        "sales",
        ($) => $.programs.metric_option["Sales"],
        "number",
        true,
        "currency",
      ),
      ctr: makeBiddingConditionOption("ctr", "ctr", ($) => $.programs.metric_option["CTR"], "number", true, "percent"),
      conv_rate: makeBiddingConditionOption(
        "conv_rate",
        "conv_rate",
        ($) => $.programs.metric_option["Conversion Rate"],
        "number",
        true,
        "percent",
      ),
      roas: makeBiddingConditionOption("roas", "roas", ($) => $.programs.metric_option["ROAS"]),
      ad_spend: makeBiddingConditionOption(
        "ad_spend",
        "ad_spend",
        ($) => $.programs.metric_option["Ad spend"],
        "number",
        true,
        "currency",
      ),
      ad_sales: makeBiddingConditionOption(
        "ad_sales",
        "ad_sales",
        ($) => $.programs.metric_option["Ad sales"],
        "number",
        true,
        "currency",
      ),
      cpc: makeBiddingConditionOption("cpc", "cpc", ($) => $.programs.metric_option["CPC"], "number", true, "currency"),
      units_sold: makeBiddingConditionOption(
        "units_sold",
        "units_sold",
        ($) => $.programs.metric_option["Units sold"],
        "number",
        true,
      ),
    };

    const metricsByEntityType = {
      advertisement: [
        "acos",
        "impressions",
        "clicks",
        "cost",
        "conversions",
        "sales",
        "ctr",
        "conv_rate",
        "roas",
        "ad_spend",
        "ad_sales",
        "cpc",
      ],
      keyword: [
        "acos",
        "impressions",
        "clicks",
        "cost",
        "conversions",
        "sales",
        "ctr",
        "conv_rate",
        "roas",
        "ad_spend",
        "cpc",
      ],
      target: [
        "acos",
        "impressions",
        "clicks",
        "cost",
        "conversions",
        "sales",
        "ctr",
        "conv_rate",
        "roas",
        "ad_spend",
        "cpc",
      ],
      product: ["acos", "impressions", "clicks", "conversions", "units_sold", "ad_sales"],
    };

    return (metricsByEntityType[type] ?? metricsByEntityType.advertisement).map((key) => metric[key]);
  };

  const optionsByEngine = {
    pricing: [
      {
        value: `\${units}`,
        label: ($) => $.programs.metric_option["Units"],
        group: "units",
        supportedOperators: OPERATOR_OPTIONS["number"],
      },
      {
        group: "bsr",
        value: `\${bsr}`,
        label: ($) => $.programs.metric_option["Average BSR"],
        supportedOperators: OPERATOR_OPTIONS["number"],
      },
    ],
    bidding: [
      makeTargetConditionOption("target_match_types", ($) => $.programs.metric_option["Targets"]),
      makeTargetConditionOption("keyword_match_types", ($) => $.programs.metric_option["Keywords"]),
      makeTargetConditionOption("tags_enhanced", ($) => $.programs.metric_option["Tags"]),
      makeBiddingConditionOption(
        "ad_sales",
        "ad_sales",
        ($) => $.programs.metric_option["Ad sales"],
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption(
        "ad_sales_trend",
        "ad_sales_trend",
        ($) => $.programs.metric_option["Ad sales trend %"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("conversions", "conversions", ($) => $.programs.metric_option["Conversions"]),
      makeBiddingConditionOption(
        "conversions_trend",
        "conversions_trend",
        ($) => $.programs.metric_option["Conversion trend %"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("acos", "acos", ($) => $.programs.metric_option["ACOS"], "number", true, "percent"),
      makeBiddingConditionOption("roas", "roas", ($) => $.programs.metric_option["ROAS"]),
      makeBiddingConditionOption(
        "ad_spend",
        "ad_spend",
        ($) => $.programs.metric_option["Ad spend"],
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption(
        "conv_rate",
        "conv_rate",
        ($) => $.programs.metric_option["Conversion rate"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption(
        "ctr",
        "click_thru_rate",
        ($) => $.programs.metric_option["Click-through rate"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption(
        "revenue_per_click",
        "revenue_per_click",
        ($) => $.programs.metric_option["Revenue per Click"],
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption("clicks", "clicks", ($) => $.programs.metric_option["Clicks"]),
      makeBiddingConditionOption(
        "clicks_trend",
        "clicks_trend",
        ($) => $.programs.metric_option["Clicks trend %"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption(
        "bid",
        "bid",
        ($) => $.programs.metric_option["Current Bid"],
        "number",
        false,
        "currency",
      ),
      makeBiddingConditionOption("impressions", "impressions", ($) => $.programs.metric_option["Impressions"]),
      makeBiddingConditionOption(
        "impressions_trend",
        "impressions_trend",
        ($) => $.programs.metric_option["Impression trend %"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("cpc", "cpc", ($) => $.programs.metric_option["CPC"], "number", true, "currency"),
      makeBiddingConditionOption("tacos", "tacos", ($) => $.programs.metric_option["TACOS"], "number", true, "percent"),
      makeBiddingConditionOption("troas", "troas", ($) => $.programs.metric_option["TROAS"]),
      makeBiddingConditionOption(
        "total_cr",
        "total_cr",
        ($) => $.programs.metric_option["Total conversion rate"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption(
        "ad.serving_hours",
        "ad.serving_hours",
        ($) => $.programs.metric_option["Campaign Serving Hours"],
      ),
      makeBiddingConditionOption(
        "ad.budget_percent",
        "ad.budget_percent",
        ($) => $.programs.metric_option["Campaign Spend % of Budget"],
        "number",
        true,
        "percent",
      ),
    ],
    budget: [
      makeBiddingConditionOption(
        "ad_sales",
        "ad_sales",
        ($) => $.programs.metric_option["Ad sales"],
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption(
        "ad_sales_trend",
        "ad_sales_trend",
        ($) => $.programs.metric_option["Ad sales trend %"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("conversions", "conversions", ($) => $.programs.metric_option["Conversions"]),
      makeBiddingConditionOption(
        "conversions_trend",
        "conversions_trend",
        ($) => $.programs.metric_option["Conversion trend %"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("acos", "acos", ($) => $.programs.metric_option["ACOS"], "number", true, "percent"),
      makeBiddingConditionOption("roas", "roas", ($) => $.programs.metric_option["ROAS"]),
      makeBiddingConditionOption("impressions", "impressions", ($) => $.programs.metric_option["Impressions"]),
      makeBiddingConditionOption(
        "impressions_trend",
        "impressions_trend",
        ($) => $.programs.metric_option["Impression trend %"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption(
        "ad_spend",
        "ad_spend",
        ($) => $.programs.metric_option["Ad spend"],
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption(
        "conv_rate",
        "conv_rate",
        ($) => $.programs.metric_option["Conversion rate"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption(
        "ctr",
        "click_thru_rate",
        ($) => $.programs.metric_option["Click-through rate"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption(
        "revenue_per_click",
        "revenue_per_click",
        ($) => $.programs.metric_option["Revenue per Click"],
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption("clicks", "clicks", ($) => $.programs.metric_option["Clicks"]),
      makeBiddingConditionOption(
        "clicks_trend",
        "clicks_trend",
        ($) => $.programs.metric_option["Clicks trend %"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption("serving_hours", "serving_hours", ($) => $.programs.metric_option["Serving Hours"]),
      makeBiddingConditionOption(
        "budget_percent",
        "budget_percent",
        ($) => $.programs.metric_option["Spend % of Budget"],
        "number",
        true,
        "percent",
      ),
      ...[
        [
          "PLACEMENT_TOP",
          {
            "Current Multiplier": ($) => $.programs.metric_option["Top of Search: Current Multiplier"],
            ACOS: ($) => $.programs.metric_option["Top of Search: ACOS"],
            ROAS: ($) => $.programs.metric_option["Top of Search: ROAS"],
            ad_spend: ($) => $.programs.metric_option["Top of Search: ad spend"],
            conv_rate: ($) => $.programs.metric_option["Top of Search: conversion rate"],
            clicks: ($) => $.programs.metric_option["Top of Search: clicks"],
            click_share: ($) => $.programs.metric_option["Top of Search: click share"],
            impressions: ($) => $.programs.metric_option["Top of Search: impressions"],
            impression_share: ($) => $.programs.metric_option["Top of Search: impression share"],
            ctr: ($) => $.programs.metric_option["Top of Search: click-through rate"],
            conversions: ($) => $.programs.metric_option["Top of Search: conversions"],
            revenue_per_click: ($) => $.programs.metric_option["Top of Search: Revenue per Click"],
          },
        ],
        [
          "PLACEMENT_PRODUCT_PAGE",
          {
            "Current Multiplier": ($) => $.programs.metric_option["Detail Page: Current Multiplier"],
            ACOS: ($) => $.programs.metric_option["Detail Page: ACOS"],
            ROAS: ($) => $.programs.metric_option["Detail Page: ROAS"],
            ad_spend: ($) => $.programs.metric_option["Detail Page: ad spend"],
            conv_rate: ($) => $.programs.metric_option["Detail Page: conversion rate"],
            clicks: ($) => $.programs.metric_option["Detail Page: clicks"],
            click_share: ($) => $.programs.metric_option["Detail Page: click share"],
            impressions: ($) => $.programs.metric_option["Detail Page: impressions"],
            impression_share: ($) => $.programs.metric_option["Detail Page: impression share"],
            ctr: ($) => $.programs.metric_option["Detail Page: click-through rate"],
            conversions: ($) => $.programs.metric_option["Detail Page: conversions"],
            revenue_per_click: ($) => $.programs.metric_option["Detail Page: Revenue per Click"],
          },
        ],
        [
          "PLACEMENT_REST_OF_SEARCH",
          {
            "Current Multiplier": ($) => $.programs.metric_option["Rest of Search: Current Multiplier"],
            ACOS: ($) => $.programs.metric_option["Rest of Search: ACOS"],
            ROAS: ($) => $.programs.metric_option["Rest of Search: ROAS"],
            ad_spend: ($) => $.programs.metric_option["Rest of Search: ad spend"],
            conv_rate: ($) => $.programs.metric_option["Rest of Search: conversion rate"],
            clicks: ($) => $.programs.metric_option["Rest of Search: clicks"],
            click_share: ($) => $.programs.metric_option["Rest of Search: click share"],
            impressions: ($) => $.programs.metric_option["Rest of Search: impressions"],
            impression_share: ($) => $.programs.metric_option["Rest of Search: impression share"],
            ctr: ($) => $.programs.metric_option["Rest of Search: click-through rate"],
            conversions: ($) => $.programs.metric_option["Rest of Search: conversions"],
            revenue_per_click: ($) => $.programs.metric_option["Rest of Search: Revenue per Click"],
          },
        ],
      ].reduce((conditions, [placement, labels]) => {
        conditions.push(
          makeBiddingConditionOption(
            `${placement}`,
            `${placement}`,
            labels["Current Multiplier"],
            "number",
            false,
            "percent",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.acos`,
            `${placement}.acos`,
            labels["ACOS"],
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(makeBiddingConditionOption(`${placement}.roas`, `${placement}.roas`, labels["ROAS"]));
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.ad_spend`,
            `${placement}.ad_spend`,
            labels["ad_spend"],
            "number",
            true,
            "currency",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.conv_rate`,
            `${placement}.conv_rate`,
            labels["conv_rate"],
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(makeBiddingConditionOption(`${placement}.clicks`, `${placement}.clicks`, labels["clicks"]));
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.click_share`,
            `${placement}.click_share`,
            labels["click_share"],
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(`${placement}.impressions`, `${placement}.impressions`, labels["impressions"]),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.impression_share`,
            `${placement}.impression_share`,
            labels["impression_share"],
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.ctr`,
            `${placement}.click_thru_rate`,
            labels["ctr"],
            "number",
            true,
            "percent",
          ),
        );
        conditions.push(
          makeBiddingConditionOption(`${placement}.conversions`, `${placement}.conversions`, labels["conversions"]),
        );
        conditions.push(
          makeBiddingConditionOption(
            `${placement}.revenue_per_click`,
            `${placement}.revenue_per_click`,
            labels["revenue_per_click"],
            "number",
            true,
            "currency",
          ),
        );
        return conditions;
      }, []),
    ],
    lineitem_bidding: [
      makeBiddingConditionOption("tacos", "tacos", ($) => $.programs.metric_option["total ACOS"]),
      makeBiddingConditionOption("troas", "troas", ($) => $.programs.metric_option["total ROAS"]),
      makeBiddingConditionOption("ctr", "click_thru_rate", ($) => $.programs.metric_option["Click-through rate"]),
      makeBiddingConditionOption("impressions", "impressions", ($) => $.programs.metric_option["Impressions"]),
      makeBiddingConditionOption("clicks", "clicks", ($) => $.programs.metric_option["Clicks"]),
      makeBiddingConditionOption("cost", "cost", ($) => $.programs.metric_option["Cost"]),
      makeBiddingConditionOption("ecpm", "ecpm", ($) => $.programs.metric_option["eCPM"]),
      makeBiddingConditionOption(
        "total_detailed_page_view_rate",
        "total_detailed_page_view_rate",
        ($) => $.programs.metric_option["total DPV rate"],
      ),
      makeBiddingConditionOption(
        "total_add_to_cart_rate",
        "total_add_to_cart_rate",
        ($) => $.programs.metric_option["total ATC rate"],
      ),
      makeBiddingConditionOption(
        "new_to_brand_detail_page_view_rate",
        "new_to_brand_detail_page_view_rate",
        ($) => $.programs.metric_option["NTB DPV rate"],
      ),
      makeBiddingConditionOption(
        "total_new_to_brand_dpv_rate",
        "total_new_to_brand_dpv_rate",
        ($) => $.programs.metric_option["total NTB DPV rate"],
      ),
      makeBiddingConditionOption(
        "brand_search_rate",
        "brand_search_rate",
        ($) => $.programs.metric_option["Brand Search Rate"],
      ),
      makeBiddingConditionOption(
        "total_units_sold",
        "total_units_sold",
        ($) => $.programs.metric_option["Total Units Sold"],
      ),
      makeBiddingConditionOption(
        "viewable_impressions",
        "viewable_impressions",
        ($) => $.programs.metric_option["Viewable Impressions"],
      ),
      makeBiddingConditionOption("viewability", "viewability", ($) => $.programs.metric_option["Viewability"]),
    ],
    tagging: makeTaggingConditionOptions(entityType),
    harvesting_query_match: [makeSearchQueryConditionOption("query", ($) => $.programs.metric_option["Search Query"])],
    harvesting: [
      makeBiddingConditionOption("conversions", "conversions", ($) => $.programs.metric_option["Conversions"]),
      makeBiddingConditionOption("units_sold", "units_sold", ($) => $.programs.metric_option["Units sold"]),
      makeBiddingConditionOption("clicks", "clicks", ($) => $.programs.metric_option["Clicks"]),
      makeBiddingConditionOption("impressions", "impressions", ($) => $.programs.metric_option["Impressions"]),
      makeBiddingConditionOption(
        "ad_spend",
        "ad_spend",
        ($) => $.programs.metric_option["Ad spend"],
        "number",
        true,
        "currency",
      ),
      makeBiddingConditionOption("acos", "acos", ($) => $.programs.metric_option["ACOS"], "number", true, "percent"),
      makeBiddingConditionOption("roas", "roas", ($) => $.programs.metric_option["ROAS"]),
      makeBiddingConditionOption(
        "conv_rate",
        "conv_rate",
        ($) => $.programs.metric_option["Conversion rate"],
        "number",
        true,
        "percent",
      ),
      makeBiddingConditionOption(
        "ctr",
        "click_thru_rate",
        ($) => $.programs.metric_option["Click-through rate"],
        "number",
        true,
        "percent",
      ),
    ],
  };

  return optionsByEngine[engine];
};

export default makeConditionOptions;
